import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { dateConfig } from "src/common/util/DateConfig";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import { useAppDispatch } from "../../common/state/hooks";
import { languageConfig } from "../../common/util/langConfig";
import { prettifyFileSizeInBytes } from "../../common/util/prettifyFileSizeInBytes";
import { getCurrentUser } from "../../features/profile/state/profileSlice";
import {
  getCurrentInteractionElements,
  getCurrentInteractions,
  getCurrentProject,
  getProjectSize,
} from "../../features/projects/state/projectsSlice";
import { ILanguage } from "../../model/model";
import InformationComponent from "../ProjectGroups/InformationComponent";
import InteractionsList from "./InteractionsList";
import ReactMarkdown from "react-markdown";
import { ButtonText } from "src/common/components/Button";

export const PROJECT_ELEMENTS_COMPONENT_ID = "projectElements";
export const PROJECT_SIZE_COMPONENT_ID = "projectSize";

const ProjectInfo = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser = useSelector(getCurrentUser);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);
  const currentProject = useSelector(getCurrentProject);
  const getElementsInteractions = useSelector(getCurrentInteractionElements);
  const [showAiPromptBasis, setShowAiPromptBasis] = useState(false);

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(
        getCurrentInteractions({
          projectId: currentProject.id,
          componentId: PROJECT_ELEMENTS_COMPONENT_ID,
        }),
      );
      dispatch(
        getProjectSize({
          projectId: currentProject.id,
          componentId: PROJECT_SIZE_COMPONENT_ID,
        }),
      );
    }
  }, [dispatch, currentProject?.id]);

  const currentProjectCreator = currentProject;
  const createdByTranslate = currentProjectCreator?.created_by
    ? currentProjectCreator.created_by
    : t("general.noCreator");

  const getDateCurrentLocale = (date: string) =>
    new Date(date).toLocaleDateString(dateConfig(currentLocale));

  return (
    <IsLoading componentId={PROJECT_SIZE_COMPONENT_ID} showSuccess={false}>
      <div className="relative grow w-full px-4 xl:px-0 mx-auto overflow-x-auto personalize-scroll-visible">
        <div className="ml-4">
          <h2 className="text-2xl font-medium mb-2 mt-4">Description</h2>
          <h3 className="py-3">{t("general.informationModule")}</h3>

          <InformationComponent
            title={t("general.moduleName")}
            moduleName={currentProject?.name || ""}
          />
          <InformationComponent
            title={t("general.weight")}
            moduleName={prettifyFileSizeInBytes(
              getElementsInteractions?.sizeInMbs || 0,
              currentLocale,
            )}
            infoTooltipText={t("tooltip.mediasWeightDetail")}
          />
          {currentProjectCreator?.creator && (
            <InformationComponent title={t("general.creator")} moduleName={createdByTranslate} />
          )}
          <InformationComponent
            title={t("general.createdAt")}
            moduleName={getDateCurrentLocale(currentProject?.createdAt || "")}
          />
          <InformationComponent
            title={t("general.updatedAt")}
            moduleName={getDateCurrentLocale(currentProject?.updatedAt || "")}
          />
          {currentProject?.cloned_from?.name && (
            <InformationComponent
              title={t("general.duplicatedFrom")}
              moduleName={currentProject.cloned_from.name}
            />
          )}

          {currentProject?.metadata?.aiPromptBasis && (
            <div className="mt-2 border-gray-300">
              <div className="w-full flex flex-col sm:flex-row items-center py-2 px-2 xl:py-3 xl:px-4 border-t border-gray-300">
                <p className="w-full sm:w-1/3 text-sm text-center sm:text-left">
                  {t("pages.projects.aiPromptBasis")}
                </p>
                <div className="w-full">
                  <div className="mt-1 mb-3">
                    {!showAiPromptBasis ? (
                      <ButtonText
                        text={t("general.showMore")}
                        onClick={() => setShowAiPromptBasis(true)}
                      />
                    ) : (
                      <div className="prose max-w-full break-words">
                        <ReactMarkdown>{currentProject.metadata.aiPromptBasis}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="relative grow w-full px-4 xl:px-0 mx-auto">
            <h2 className="text-2xl font-medium mb-2 mt-4 xl:mt-8">Elements</h2>
            <h3 className="py-3">{t("general.interactionsElements")}</h3>
            <InteractionsList interactions={getElementsInteractions?.interactions} />
          </div>
        </div>
      </div>
    </IsLoading>
  );
};

export default ProjectInfo;
